import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  FormControl,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { ChargingBASE_URL } from "../../Config";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

// Define columns for the DataTable
const columns = [
  {
    name: "Claim Number",
    selector: (row) => row.claimNumber,
    sortable: true,
  },
  {
    name: "Name",
    selector: (row) => row.name,
    sortable: true,
  },
  {
    name: "Mobile Number",
    selector: (row) => row.mobileNumber,
    sortable: true,
  },
  {
    name: "Service Name",
    selector: (row) => row.serviceName,
    sortable: true,
  },
  {
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
  },
  {
    name: "Paid Amount",
    selector: (row) => row.paidAmount,
    sortable: true,
  },{
    name: "Claim Date",
    selector: (row) => row.claimDate,
    sortable: true,
  },
  {
    name: "Claim Date",
    selector: (row) => row.claimDate,
    sortable: true,
  },
  {
    name: "Close Date",
    selector: (row) => row.approvedDate,
    sortable: true,
  },
  {
    name: "Reason of Rejection",
    selector: (row) => row.reasonOfRejection,
    sortable: true,
    cell: (row) => (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-reason-${row.claimNumber}`}>
            {row.reasonOfRejection || "N/A"}
          </Tooltip>
        }
      >
        <span
          className="text-truncate"
          style={{
            maxWidth: "150px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.reasonOfRejection || "N/A"}
        </span>
      </OverlayTrigger>
    ),
  },
];

const JazzcashClaim = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(1);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [serviceName, setServiceName] = useState(""); // Dropdown field for service name
  const [status, setStatus] = useState("");
  const [userId, setUserId] = useState("");
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [searchTerm, setSearchTerm] = useState(""); // Added searchTerm for filtering

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user?.userId) {
      setUserId(user.userId);
    } else {
      setError("User not logged in");
    }
  }, []);

  // Fetch data when the user applies filters
  const fetchData = async () => {
    if (!userId || !filtersApplied) return;

    // At least one filter should be applied
    if (!startDate && !endDate && !mobileNumber && !serviceName && !status) {
      setError("Please fill in at least one filter field.");
      return;
    }

    const url = new URL(`${ChargingBASE_URL}ClaimReport`);
    const params = {
      UserId: userId,
      StartDate: startDate,
      EndDate: endDate || "",
      mobileNumber,
      serviceName,
      status,
    };

    // Construct URL with parameters
    url.search = new URLSearchParams(params).toString();

    try {
      setIsLoading(true);
      setProgress(1);

      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prevProgress + 2;
        });
      }, 100);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch data.");
      }

      const result = await response.json();
      setData(result.data);
      setFilteredData(result.data);
      setError(null);
    } catch (error) {
      setError(error.message);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setFiltersApplied(true);
    fetchData();
  };

  const handleResetFilters = () => {
    // Reset all filter states
    setStartDate("");
    setEndDate("");
    setMobileNumber("");
    setServiceName("");
    setStatus("");
    setSearchTerm(""); // Clear search term as well
    setFilteredData([]); // Optionally clear filtered data
    setError(null);
    setFiltersApplied(false);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(term)
      )
    );
    setFilteredData(filtered); // Update filtered data based on the search term
  };

  return (
    <>
      {isLoading && (
        <div className="text-center mt-2">
          <ProgressBar
            animated
            now={progress}
            label={`Loading Data... ${progress}%`}
            style={{ width: "100%" }}
          />
        </div>
      )}

      {error && <div className="text-center mt-2">{error}</div>}

      <Row className="justify-content-center">
        <Col lg={10} md={12} sm={12}>
          <Card className="mt-2 shadow-lg">
            <Card.Body>
              <Form>
                <Row className="mb-3">
                  <Col md={3} sm={6}>
                    <Form.Group controlId="startDate">
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Group controlId="endDate">
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Group controlId="mobileNumber">
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Group controlId="serviceName">
                      <Form.Label>Service Name</Form.Label>
                      <Form.Control
                        as="select"
                        value={serviceName}
                        onChange={(e) => setServiceName(e.target.value)}
                      >
                        <option value="">Select Service</option>
                        <option value="JazzCash_Sehat_Salamti">
                          JazzCash_Sehat_Salamti
                        </option>
                        <option value="JazzCash_NISA_HEALTH">
                          JazzCash_NISA_HEALTH
                        </option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Form.Group controlId="status">
                      <Form.Label>Status</Form.Label>
                      <Form.Control
                        as="select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="">Select Status</option>
                        <option value="Intimation">Processing</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={3} sm={6}>
                    <Button
                      variant="primary"
                      onClick={handleApplyFilters}
                      className="mt-4 w-100"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Search Records"
                      )}
                    </Button>
                  </Col>
                  <Col md={3} sm={6}>
                    <Button
                      variant="secondary"
                      onClick={handleResetFilters}
                      className="mt-4 w-100"
                    >
                      Reset Filters
                    </Button>
                  </Col>
                </Row>
              </Form>

              {/* Search Bar */}
              {!isLoading && (
                <InputGroup className="mb-3">
                  <FormControl
                    placeholder="Search records"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </InputGroup>
              )}

              {/* DataTable */}
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                responsive
                highlightOnHover
                striped
                dense
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 15, 20, 50]}
                defaultSortFieldId={1}
                defaultSortAsc={true}
              />

              {/* Download CSV Button */}
              {filteredData.length > 0 && !isLoading && (
                <CSVLink
                  data={filteredData}
                  filename={"claim_data.csv"}
                  className="btn btn-success mt-1 w-100"
                >
                  Download CSV
                </CSVLink>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default JazzcashClaim;
